import { Button } from "components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./loan-card.scss";
import classNames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ApplicationLoanStatus } from "pages/application/applicationService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";


interface LoanDetailProps {
  label: string;
  value: string;
  showTooltip?: boolean;
}

const LoanDetail = ({ label, value, showTooltip }: LoanDetailProps) => (
  <div className="loanCard-wrapper__detail">
    <span className="loanCard-wrapper__keyName">{label}:</span>
    <span className="loanCard-wrapper__value" title={showTooltip ? value : undefined}>
      {value}
    </span>
  </div>
);

const getStatusIcon = (status: ApplicationLoanStatus) => {
  const icons: Record<ApplicationLoanStatus, IconProp> = {
    new: solid("flag-checkered"),
    incomplete: solid("hexagon-exclamation"),
    started: solid("paper-plane"),
    processing: solid("paper-plane"),
    submittedToUnderwriting: solid("paper-plane"),
    underReview: solid("paper-plane"),
    conditionallyApproved: solid("paper-plane"),
    finalReview: solid("paper-plane"),
    clearedToClosed: solid("paper-plane"),
    funded: solid("piggy-bank"),
    expired: solid("triangle-exclamation"),
  };

  return <FontAwesomeIcon icon={icons[status]} />;
};
const formatStatus = (status: string) => {
  const spacedStatus = status.replace(/([a-z])([A-Z])/g, "$1  $2");
  const formatted = spacedStatus.charAt(0).toUpperCase() + spacedStatus.slice(1);
  return formatted.replace(/\bTo\b/g, "to");
};

interface LoanCardProps {
  readonly property: string;
  readonly loanPurpose: string;
  readonly loanAmount: string;
  readonly status: ApplicationLoanStatus;
  readonly startedDate: string;
  readonly documentStatus?: boolean;
  readonly index: number;
  readonly loanNumber: string;
  readonly nhDealApplicationId: string;
  readonly handleLoanCardClick: (index: number) => void;
  readonly applicationNumber: string;
}

export default function LoanCard({
  status,
  property,
  loanPurpose,
  loanAmount,
  startedDate,
  documentStatus,
  index,
  loanNumber,
  nhDealApplicationId,
  applicationNumber,
  handleLoanCardClick
}: LoanCardProps) {
  const statusClass = classNames({
    greeen: status === "new",
    red: status === "incomplete",
    blue: ["started", "processing", "submittedToUnderwriting", "conditionallyApproved", "underReview", "finalReview", "clearedToClosed"].includes(status),
    grey: status === "funded",
    warning: status === "expired",
  });
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  return (
    <div className="loanCard">
      <div className="loanCard-wrapper">
        <div className={`loanCard-wrapper__status ${statusClass}`}>
          <span>{getStatusIcon(status)}</span>
          <span>{formatStatus(status)}</span>
        </div>
        <LoanDetail label="Loan Number" value={loanNumber} />
        {applicationNumber && <LoanDetail label="Application Number" value={applicationNumber} />}
        {featureFlag?.showNhDealApplicationIdEnabled && <LoanDetail label="ID#" value={nhDealApplicationId.slice(-12)} />}
        <LoanDetail label="Property" value={property} />
        <LoanDetail label="Loan Purpose" value={loanPurpose} />
        <LoanDetail label="Amount" value={loanAmount} />
        <LoanDetail label="Started" value={startedDate} />

        <div className="loanCard-wrapper__message">
          {documentStatus ? <div className="loanCard-wrapper__doc-notify">New document available</div> : null}
          {status === "expired" || status === "funded" ? <div className="loanCard-wrapper__contact">Contact Loan Officer for New Application</div> : null}
        </div>
      </div>
      <div className="loanCard-wrapper__selectBtn">
        <Button title="Select" onClick={() => handleLoanCardClick(index)} disabled={status === "expired" || status === "funded"} />
      </div>
    </div>
  );
}
